import { z } from "zod";

import { dropLastWhile } from "~/tools/drop-last-while";
import { filter } from "~/tools/filter";
import { flat } from "~/tools/flat";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { isEmpty } from "~/tools/is-empty";
import { isNull } from "~/tools/is-null";
import { isPresent } from "~/tools/is-present";
import type { JSON } from "~/tools/json";
import { map } from "~/tools/map";
import { pipe } from "~/tools/pipe";
import type { Record } from "~/tools/record";
import { sortBy } from "~/tools/sort-by";
import { take } from "~/tools/take";
import { toEntries } from "~/tools/to-entries";

class Encoder {
	public static primitive<T extends string | number | boolean | null>(input: undefined | T, defaults?: T): Encoder {
		const defaulted = input ?? defaults ?? null;

		return new Encoder(defaulted, defaulted === defaults);
	}

	public static list<T>(
		items: undefined | ReadonlyArray<T>,
		defaults: ReadonlyArray<T>,
		transform: (entry: T) => Encoder,
	): Encoder {
		const encoded = Encoder.dropDefaultedTail((items ?? defaults).map(transform));
		const defaulted = Encoder.dropDefaultedTail(defaults.map(transform));

		return new Encoder(encoded, isDeepEqual(encoded, defaulted));
	}

	public static tuple<const T extends ReadonlyArray<Encoder>>(...elements: T): Encoder {
		const encoded = Encoder.dropDefaultedTail(elements);

		return new Encoder(encoded, isEmpty(encoded));
	}

	/**
	 * Encode a record as a single dimensional Array with keys on even and values on odd positions.
	 * This format minimizes produced numeric fields tokens.
	 *
	 * @example
	 * Encoder.shape({
	 *   0: Encoder.primitive(42),
	 *   1: Encoder.primitive("test"),
	 * }) == [0, 42, 1, "test"]
	 * // vs {"0": 42, "1": "test"}
	 */
	public static shape<T extends Record<string | number, Encoder>>(record: T): Encoder {
		const encoded = pipe(
			record,
			toEntries(),
			// Sort is lexicographic on stringified keys (e.g. "0","1","10","11","2",...).
			// Pair order is irrelevant for correctness — the decoder re-pairs purely
			// positionally via `chunk(2) → fromEntries`. The sort exists only to make
			// the encoded output stable and deterministic for identical inputs.
			sortBy(([key]) => key),
			map(([key, encoder]) => {
				const value = encoder.encode();

				if (value == null) {
					return null;
				}

				return [z.coerce.number().safeParse(key).data ?? key, value];
			}),
			filter(isPresent),
			flat(),
		);

		return new Encoder(encoded, isEmpty(encoded));
	}

	private static dropDefaultedTail(elements: ReadonlyArray<Encoder>): ReadonlyArray<JSON> {
		const defaulted = dropLastWhile(
			elements.map((encoder) => encoder.encode()),
			isNull,
		);

		// drop the defaulted tail and take the rest without default values
		return pipe(
			elements,
			map((encoder) => encoder.value),
			take(defaulted.length),
		);
	}

	private constructor(
		private readonly value: JSON,
		private readonly isDefault: boolean,
	) {}

	public encode(): JSON {
		return this.isDefault ? null : this.value;
	}
}

export { Encoder };
